"use strict";
const EventEmitter = require("events");
const _ = require("lodash");
const toRegexRange = require("to-regex-range");
const moment = require("moment");
const Renderer = require("./FilterRenderer");

class FilterComponent extends EventEmitter {
    constructor(entityGrid, columnsData, logger, initialOptions, advancedSearchData) {
        super();
        this._entityGrid = entityGrid;
        this._logger = logger;
        this._columnsData = columnsData || [];
        this._columndsDataByName = this._columnsData.reduce(
            (obj, col) => Object.assign({}, obj, {[col.name]: col}),
            {}
        );
        this.filterData = {};

        this._setFilterData(_.pick(initialOptions, ["string", "isRegEx"]), {silent: true});
        const gridColumnOrder = this._entityGrid.getColumnOrder();

        this.analyzeString = this.analyzeString.bind(this);
        this._onColumnOrderChanged = this._onColumnOrderChanged(gridColumnOrder).bind(this);
        this._buildQuery = this._buildQuery.bind(this);
        this._singleSubQuery = this._singleSubQuery.bind(this);
        this._onAdvancedFilterChanged = this._onAdvancedFilterChanged.bind(this);

        this._buildCondition = _.curry(this._buildCondition.bind(this));
        this._buildRegexCondition = _.curry(this._buildRegexCondition.bind(this));

        this._renderer = new Renderer(this, initialOptions, advancedSearchData);
        this._registerListeners();

        this._localDateFormats = this.getLocalDateFormat();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/
    _registerListeners() {
        this._renderer.on("FilterStringChange", this.analyzeString);
        this._entityGrid.on("ColumnOrder", this._onColumnOrderChanged);
        this._entityGrid.on("AdvancedSearchFilterChangeData", this._onAdvancedFilterChanged);
    }
    _unregisterListeners() {
        this._renderer.removeListener("FilterStringChange", this.analyzeString);
        this._entityGrid.removeListener("ColumnOrder", this._onColumnOrderChanged);
        this._entityGrid.removeListener(
            "AdvancedSearchFilterChangeData",
            this._onAdvancedFilterChanged
        );
    }

    _setFilterData(filterData, options = {}) {
        this._filterData = _.defaults(filterData, {
            string: "",
            isRegEx: false,
        });
        if (!options.silent) {
            this.emit("FilterDataChange", filterData);
        }
    }

    _triggerAdvancedFilterChanged(advancedFilterQuery) {
        this.emit("AdvancedFilterChanged", advancedFilterQuery);
    }

    _triggerFilterInputIsValid(invalidState) {
        this.emit("FilterInputIsValid", invalidState);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    get logger() {
        return this._logger;
    }

    getRootElement() {
        return this.renderer.getRootElement();
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = null;
                this._columnsData = null;
                this._columndsDataByName = null;
                this._filterData = null;
                this._renderer = null;
            },
            (err) => {
                this._logger.warn(err, "Filter.shutdown error");
            }
        );
    }

    render() {
        return this._renderer.render();
    }

    /**
     * Analyzes the search string. Extracts quoted substrings and subExpressions in parentheses and replaces it in order to process it later.
     * Creates search query for each column and concatenates them with $or operator.
     * Fires 'BasicSearchFilterChange' event on this component.
     * @param {Object} filterData
     * @param {string} filterData.string - search string
     * @param {boolean} filterData.isRegEx - is regular expression flag
     * @param {boolean} [forced=false] - flag to force analyze even if string haven't been changed
     * @param {boolean} [silent=false] - flag to to emit event 'BasicSearchFilterChange' or not
     * @returns {Object} -- query object
     */
    analyzeString(filterData, forced = false, silent = false) {
        const {string, isRegEx} = filterData || {};
        let mongoQuery;

        if (string === this._filterData.string && !forced) {
            return {};
        }

        this._setFilterData(filterData);
        if (!string) {
            this._triggerFilterInputIsValid(true);
            return this.emit("BasicSearchFilterChange", {}, {});
        }

        if (!this.checkQuerystringValidity(string, isRegEx)) {
            this._triggerFilterInputIsValid(false);
            return false;
        }

        if (isRegEx) {
            mongoQuery = this._singleSubQuery(string, null, null, true);
        } else {
            mongoQuery = this._buildQuery(string);
        }

        const cleanQuery = this._queryCleanup(mongoQuery);

        if (!silent) {
            this.emit("BasicSearchFilterChange", filterData, cleanQuery);
        }
        this._triggerFilterInputIsValid(true);

        return cleanQuery;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onColumnOrderChanged(prevColumnOrder) {
        prevColumnOrder = prevColumnOrder || [];

        return (columnOrder) => {
            const columnsAreChanged =
                prevColumnOrder.length !== columnOrder.length ||
                _.difference(prevColumnOrder, columnOrder).length;
            if (columnsAreChanged && this._filterData.string) {
                this.analyzeString(this._filterData, true);
            }
            prevColumnOrder = columnOrder;
        };
    }

    _onAdvancedFilterChanged(advancedFilterQuery) {
        //this._setFilterData({});
        this._triggerAdvancedFilterChanged(advancedFilterQuery);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getColumnsToFilter() {
        const columnsDataByName = this._columndsDataByName;
        const userColumns = this._entityGrid.getColumns();
        const visibleColumns = this._entityGrid.getColumnOrder();

        return visibleColumns.reduce((columnsData, colName) => {
            const fieldName = userColumns[colName].path || colName;
            return columnsDataByName[fieldName]
                ? columnsData.concat(columnsDataByName[fieldName])
                : columnsData;
        }, []);
    }

    /**
     * Splits the search string to subsets and builds search query from conditions for the specified column
     * @param {string} string - search string
     * @param {Object} [quotedSubstrings = null] - set of quoted substrings
     * @param {Object} [parenthesesSubstrings = null] - set of expressions in parentheses
     * @private
     */
    _buildQuery(string, quotedSubstrings = null, parenthesesSubstrings = null) {
        if (!quotedSubstrings) {
            quotedSubstrings = {};
            string = string.replace(
                /(?:^|\s|\()("(?:"|.+?[^\\]"))(?=\s|$|\))/g,
                this._replaceSubset(quotedSubstrings, '%"%')
            ); //find and replace all quoted stand alone phrases
        }
        if (!parenthesesSubstrings) {
            parenthesesSubstrings = {};
            string = string.replace(
                /(?:^|\s)(\([^)]*\))(?=\s|$)/g,
                this._replaceSubset(parenthesesSubstrings, "%()%")
            ); //find and replace all parentheses expressions
        }

        let query = {};

        let orSubstrings = string
            .split(/\bOR\b|\|/) // jshint ignore:line
            .map((s) => _.trim(s))
            .filter(Boolean);

        query.$or = orSubstrings.map((orSubset) => {
            let query = {};

            let andSubstrings = orSubset
                .split(/\bAND\b|\s&\s|\s/) // jshint ignore: line
                .map((s) => _.trim(s))
                .filter(Boolean);

            query.$and = andSubstrings.map((andSubstring) =>
                this._singleSubQuery(andSubstring, quotedSubstrings, parenthesesSubstrings)
            );
            return query;
        });

        return query;
    }

    /**
     * @param {string} subset
     * @param {Object} quotedSubstrings - set of quoted substrings
     * @param {Object} parenthesesSubstrings - set of expressions in parentheses
     * @param {Boolean} [isRegEx = false] - RegEx flag
     * @returns {*}
     * @private
     */
    _singleSubQuery(subset, quotedSubstrings, parenthesesSubstrings, isRegEx = false) {
        if (parenthesesSubstrings && parenthesesSubstrings[subset] !== undefined) {
            //process subsequence in parentheses
            return this._buildQuery(
                parenthesesSubstrings[subset],
                quotedSubstrings,
                parenthesesSubstrings
            );
        }

        const columnsToFilter = this._getColumnsToFilter();
        const buildCondition = isRegEx
            ? this._buildRegexCondition(subset)
            : this._buildCondition(subset, quotedSubstrings, parenthesesSubstrings);

        return {$or: columnsToFilter.map(buildCondition)};
    }

    /**
     * build condition for specified column and string subset base on column dataType
     * @param {string} value - string subset
     * @param {Object} quotedSubstrings - set of quoted substrings
     * @param {Object} parenthesesSubstrings - set of expressions in parentheses
     * @param {Object} [colData] - column data
     * @param {string} colData.name - column name
     * @param {string} colData.dataType - column data type
     * @returns {*} Query subset for the column like {columnName: condition}
     *
     * @private
     */
    _buildCondition(value, quotedSubstrings, parenthesesSubstrings, colData) {
        const isExactMatch = _.has(quotedSubstrings, value);
        value = quotedSubstrings[value] || value;
        const {name, dataType, valuePath, options, typeInfo} = colData;
        let queryValue;
        const filterColumn = valuePath || name;
        const query = {};

        switch (dataType) {
            case "string":
                queryValue = this._stringProcess(value, isExactMatch);

                if (options) {
                    queryValue = this._selectProcess(queryValue, options);
                }
                break;
            case "number":
                queryValue = this._numberProcess(value);
                break;
            case "date":
                queryValue = this._dateProcess(value);
                break;
            case "boolean":
                queryValue = this._booleanProcess(value);
                break;
            case "enum":
                queryValue = this._enumProcess(
                    value,
                    typeInfo,
                    this._stringProcess(value, isExactMatch)
                );
                break;
            default:
                break;
        }

        if (queryValue !== null) {
            query[filterColumn] = queryValue;
        }

        return query;
    }

    /**
     * Builds regex condition for the specified column
     * @param {string} regexString - search string which will be passed as regex condition
     * @param {Object} [colData] - column data
     * @param {string} colData.name - column name
     * @param {string} colData.dataType - column data type
     * @returns {*} - Query subset for the column like {columnName: condition}
     * @private
     */
    _buildRegexCondition(regexString, colData) {
        const {name, dataType} = colData;
        const query = {};
        if (dataType === "string") {
            query[name] = {
                $regex: regexString,
                $options: "i",
            };
        }
        return query;
    }

    /**
     *
     * @param {Object} subsetObj - object to accumulate replacements and substrings
     * @param {string} [replacementKey='']
     * @param {number} [i=0] - iterator start number for the replacements
     * @returns {function(string, string): string} - function got string returns string with replacements
     * @private
     */
    _replaceSubset(subsetObj, replacementKey = "", i = 0) {
        return (match, p1) => {
            const subKey = `${replacementKey}${i++}`;
            p1 = p1.trim();
            subsetObj[subKey] = p1.substring(1, p1.length - 1).trim();

            return match.replace(p1, ` ${subKey} `);
        };
    }

    /**
     * Function to cleanup the query: remove empty subsets and move statement up from array if there is only one item.
     * @param {Object} query
     * @returns {Object}
     * @private
     */
    _queryCleanup(query) {
        const operators = ["$or", "$and"];
        const _queryCleanup = this._queryCleanup.bind(this);
        if (typeof query !== "object") {
            return query;
        }
        return Object.entries(query).reduce((cleanQuery, [operator, subQuery = null]) => {
            if (
                !operators.includes(operator) &&
                !(typeof subQuery === "object" && _.isEmpty(subQuery))
            ) {
                return Object.assign({}, cleanQuery, {[operator]: subQuery});
            }
            if (Array.isArray(subQuery)) {
                if (subQuery.length === 0) {
                    return cleanQuery;
                }

                if (subQuery.length === 1) {
                    return Object.assign({}, cleanQuery, _queryCleanup(subQuery[0]));
                }
                if (subQuery.length > 1) {
                    const subCriteria = subQuery
                        .map(_queryCleanup)
                        .filter((subQueryItem) => !_.isEmpty(subQueryItem));
                    const criteria = subCriteria.length ? {[operator]: subCriteria} : null;

                    return Object.assign({}, cleanQuery, criteria);
                }
            }
            return cleanQuery;
        }, {});
    }

    _stringProcess(str, exact) {
        let value = "";
        const regexpEscape = (string = "") => {
            return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        };
        const processVariation = (variation) => {
            return _.trim(variation, "[]")
                .replace(/(\d+)\.\.(\d+)/g, (str, n1, n2) => toRegexRange(n1, n2, {capture: true})) //replace number range to regex
                .replace(/,/g, "|")
                .replace(/^.*$/, "($&)");
        };

        if (exact) {
            value = regexpEscape(str);
        } else {
            const variationsRegExp = /\[(?:(?:(?:\d+,?)+|(?:(?:\d+\.\.\d+),?)+)+)]/g; //find sequences like [20,30,80], [5..9], [1,2,5..9], [1,5..9,15], [5..9,15,20], [2..5,7..9]

            let variations = [];
            let variationSubstrings = str.match(variationsRegExp);

            if (variationSubstrings) {
                variations = variationSubstrings.map(processVariation);
            }

            const substrings = str.split(variationsRegExp).map((substr) => {
                return regexpEscape(substr).replace(/\\\*/g, ".*").replace(/\\\?/g, ".");
            });

            value = substrings.reduce((result, substr, idx, substrArr) => {
                let numberVariationSequence = "";
                if (variations[idx]) {
                    numberVariationSequence = variations[idx];

                    if (!substrArr[idx + 1]) {
                        //if there is no chars after number variation
                        numberVariationSequence += "\\b"; // \\b is an attempt to avoid matching cases like n-[1..2] <=> n-1, n-10, n-11, n-2, n-20, etc
                    }
                }
                return result + substr + numberVariationSequence;
            }, "");
        }

        return {
            $regex: value,
            $options: exact ? "" : "i",
        };
    }

    _numberProcess(str) {
        const value = Number(str);
        return isNaN(value) ? null : value;
    }

    _dateProcess(str) {
        const dateFormat = this._localDateFormats;
        const m = moment(str, dateFormat, true);
        let queryRange = null;

        if (m.isValid()) {
            queryRange = {$gte: +m, $lt: +m.add(1, "day")};
        }

        return queryRange;
    }

    _selectProcess(valueQuery, options) {
        const {$regex, $options} = valueQuery;
        const regEx = new RegExp($regex, $options);
        let queryValue = null;

        if (regEx.test("unknown")) {
            queryValue = {
                $nin: Object.values(options)
                    .filter(({name, title}) => !(regEx.test(name) || regEx.test(title)))
                    .map(({name}) => name),
            };
        } else {
            const matchedOptions = Object.values(options)
                .filter(({name, title}) => regEx.test(name) || regEx.test(title))
                .map(({name}) => name);

            if (matchedOptions.length) {
                queryValue = {
                    $in: matchedOptions,
                };
            }
        }

        return queryValue;
    }

    _booleanProcess(str) {
        const booleanStatements = ["true", "True", "TRUE", "false", "False", "FALSE"];
        if (booleanStatements.includes(str)) {
            return String(true) === str.toLowerCase();
        }
        return null;
    }

    _enumProcess(str, typeInfo, stringProcessedQuery) {
        let queryValue = null;
        const {ids = [], labels = [], multiSelect = false} = typeInfo || {};
        const {$regex, $options} = stringProcessedQuery;

        let regEx = /^(?:)$/;

        try {
            regEx = new RegExp($regex, $options);
        } catch (e) {
            console.warn("invalid regex:", {$regex, $options});
            return queryValue;
        }

        const matchedIds = ids.filter((id, idx) => regEx.test(labels[idx]));
        if (matchedIds.length && !multiSelect) {
            queryValue = {
                $in: matchedIds,
            };
        }

        return queryValue;
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    getLocalDateFormat() {
        const testDate = new Date(1970, 2, 4);
        const localTestDateString = testDate.toLocaleDateString();
        const replaceRegEx = (val) => new RegExp(`0?${val}`);

        const dateFormat = localTestDateString
            .replace(replaceRegEx(testDate.getFullYear()), "YYYY")
            .replace(replaceRegEx(testDate.getMonth() + 1), "M")
            .replace(replaceRegEx(testDate.getDate()), "D");

        return dateFormat;
    }

    checkQuerystringValidity(string, isRegEx) {
        return isRegEx ? checkRegExp(string) : checkString(string);

        function checkRegExp(string) {
            let isValid = true;
            try {
                new RegExp(string);
            } catch (e) {
                isValid = false;
            }
            return isValid;
        }

        function checkString(string) {
            //exclude escaped spec chars ()[]|&" and quoted substring(between unescaped quotes) from analysis
            string = string
                .replace(/(?:^|[^\\])(?:[\\]{2})*?(\\["()[\]&|])/g, (fullMatch, group1) =>
                    fullMatch.replace(group1, "")
                ) //exclude escaped chars
                .replace(/(?:^|\s|\()("(?:"|.+?[^\\]"))(?=\s|$|\))/g, (fullMatch, group1) =>
                    fullMatch.replace(group1, "")
                ); //exclude quoted substrings

            //check amount of unescaped special characters:
            //1. there shouldn't be unescaped quotes
            //2. any opened parentheses should be closed
            //3. any square bracket should be closed as well
            const checkers = [
                (
                    (quotes = []) =>
                    (char) =>
                        char === '"' ? (quotes = quotes.concat(char)) : quotes
                )(),
                checkBrackets("(", ")"),
                checkBrackets("[", "]"),
            ];

            return string
                .split("")
                .reduce((checkResults, char) => checkers.map((checker) => checker(char)), [])
                .every((checkResult) => checkResult.length === 0);

            function checkBrackets(open, close) {
                let unclosedBrackets = [];
                return (char) => {
                    if (char === open || char === close) {
                        const lastItem = unclosedBrackets.slice(-1)[0];
                        if (lastItem === open && char === close) {
                            unclosedBrackets.pop();
                        } else {
                            unclosedBrackets.push(char);
                        }
                    }
                    return unclosedBrackets;
                };
            }
        }
    }
    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}
module.exports = FilterComponent;
